import { useCallback, useEffect, useRef, useState } from 'react';
import { loadTurnstileScript, TURNSTILE_SITE_KEY } from '../helpers/turnstile';
import { TurnstileStatus } from '../types/turnstile.types';

const safely = (action: () => void) => {
  try {
    action();
  } catch {
    /* the widget is no longer available*/
  }
};

export const useTurnstile = (siteKey = TURNSTILE_SITE_KEY) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<TurnstileStatus>(siteKey ? 'loading' : 'disabled');

  useEffect(() => {
    if (!siteKey) {
      setStatus('disabled');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) {
          return;
        }

        const widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (value: string) => setToken(value),
          'error-callback': () => setToken(null),
          'expired-callback': () => setToken(null),
          theme: 'auto',
          appearance: 'interaction-only'
        });

        if (!widgetId) {
          setStatus('error');
          return;
        }

        widgetIdRef.current = widgetId;
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
      setToken(null);

      if (widgetIdRef.current !== null) {
        const widgetId = widgetIdRef.current;
        widgetIdRef.current = null;
        safely(() => window.turnstile?.remove(widgetId));
      }
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken(null);

    if (widgetIdRef.current !== null) {
      const widgetId = widgetIdRef.current;
      safely(() => window.turnstile?.reset(widgetId));
    }
  }, []);

  return {
    containerRef,
    token,
    status,
    reset,
    isVerified: status === 'disabled' || Boolean(token)
  };
};
